<template>
  <div class="rounded mb-3">
    <v-layout row wrap class="business-type-services">
      <v-flex xs12>
        <div class="px-0">
          <div class="label" v-if="!hideBusinessTypeLabel">
            {{ $tc('message.bizgainOnboarding.businessType') }}
          </div>
          <v-layout>
            <v-flex xs12>
              <v-autocomplete
                ref="businessType"
                flat
                v-model="businessType"
                :search-input.sync="search"
                :items="searchResults"
                :placeholder="$tc('message.bizgainOnboarding.SearchBusinessType')"
                return-object
                :loading="loadingBusinessCategories"
                :menu-props="{ maxHeight: 200}"
                :no-filter="true"
                :no-data-text="$t('message.bizgainOnboarding.noDataFound')"
                :hide-no-data="!showNoDataMessage"
                @input="getSelectedServices"
                class="pt-0 mt-0 business-type-input"
              />
            </v-flex>
          </v-layout>
        </div>
      </v-flex>
      <v-flex xs12 v-if="suggestedCategories && suggestedCategories.length > 0" class="suggestions business-type-suggestion">
        <v-layout
          row
          wrap
        >
          <span
            lg12
            xs12
            class="small-label"
          >
            {{ $tc('message.bizgainOnboarding.suggestions') }}
          </span>
          <div
            v-for="(suggestedCategory, index) in suggestedCategories"
            :key="index"
          >
            <v-chip
              class="mx-1 suggestion"
              :class="{'selected-suggestion':businessType.value && businessType.value.id == suggestedCategory.id}"
              @click="updateBusinessType(suggestedCategory)"
            >
              <span>
                {{ suggestedCategory && suggestedCategory.displayName }}
              </span>
            </v-chip>
          </div>
        </v-layout>
      </v-flex>
      <v-flex xs12>
        <slot name="btn" />
      </v-flex>
    </v-layout>
  </div>
</template>
<script>
import {
  VLayout,
  VFlex,
  VAutocomplete,
  VChip
} from 'vuetify/lib';
import { mapActions, mapGetters } from 'vuex';
import { ApiService } from 'Features/bizgainOnboarding/services/apiService';
import debounce from 'lodash/debounce';
const { getBusinessCategories } = new ApiService();
export default {
  props: ['isSummary', 'hideBusinessTypeLabel'],
  components: {
    VLayout,
    VFlex,
    VAutocomplete,
    VChip
  },
  data(){
    return {
      businessType: null,
      search: null,
      searchResults: [],
      loadingBusinessCategories: false,
      isMenuActive: false,
      enableNoDataText: false
    }
  },
  created(){
    if (this.category) {
      let businessCategory = {
        text: this.category.displayName,
        value: this.category
      };
      this.searchResults.push(businessCategory);
      this.businessType = businessCategory;
    } else {
      this.search = '';
    }
  },
  mounted(){
    // v-autocomplete does not have event for blur and focus
    let businessTypeInput = this.$refs.businessType.$refs.input;
    businessTypeInput.onfocus = this.onFocus;
    businessTypeInput.onblur = this.onBlur;
  },
  computed: {
    ...mapGetters(['suggestedCategories', 'category', 'services', 'suggestedServices', 'businessDetails', 'isError']),
    showNoDataMessage(){
      return this.enableNoDataText && !this.loadingBusinessCategories;
    }
  },
  methods: {
    ...mapActions(['setCurrentSection', 'setCurrentRoute', 'patchBusinessDetails', 'updateMessage', 'clearMessages']),
    onFocus(){
      this.isMenuActive = true;
    },
    onBlur() {
      this.isMenuActive = false;
    },
    getCategories: debounce(async function(keyword) {
      try {
        this.searchResults = [];
        this.loadingBusinessCategories = true;
        this.$emit('update-loadingBusinessCategories', this.loadingBusinessCategories);
        this.enableNoDataText = true;
        this.clearMessages();
        let businessCategories = await getBusinessCategories(keyword);
        if (businessCategories && Array.isArray(businessCategories) && businessCategories.length > 0) {
          this.searchResults = businessCategories.map(businessCategory => {
            return {
              text: businessCategory.displayName,
              value: businessCategory
            }
          });
        }
        let customKeyword = keyword.replace(/[^a-zA-Z ]/g, '');
        let customBusinessType = {
          text: 'Use \'' + customKeyword + '\'',
          value: {
            displayName: customKeyword
          }
        }
        this.searchResults = [customBusinessType, ...this.searchResults]
      } catch (error) {
        this.updateMessage({ type: 'error', message: this.$t('message.bizgainOnboarding.failedToFetchBusinessTypes') });
      } finally {
        this.loadingBusinessCategories = false;
        this.$emit('update-loadingBusinessCategories', this.loadingBusinessCategories);
      }
    }, 300),
    updateBusinessType(category) {
      this.searchResults = [];
      let businessCategory = {
        text: category.displayName,
        value: category
      };
      this.searchResults.push(businessCategory);
      this.businessType = businessCategory;
      this.$emit('add-businessType', category);
    },
    getSelectedServices() {
      // to remove additional text in the text field
      this.businessType.text = this.businessType.value.displayName;
      // to remove the focus from v-autocomplete refer this :https://stackoverflow.com/a/58280593
      this.$refs.businessType.$refs.input.blur();
      this.isMenuActive = false;
      let services = this.businessType && this.businessType.value && this.businessType.value.serviceTypes;
      if (services && Array.isArray(services) && services.length > 0) {
        if(!this.isSummary){
          this.$emit('add-suggestions', services.map(x => x.displayName));
        }
      }
    },
    itemClick(){
      this.$refs.businessType.$refs.input.blur();
      this.isMenuActive = false;
    }
  },
  watch: {
    search(newValue) {
      if(!newValue){
        this.enableNoDataText = false;
        // if the search text is removed then we disable no data message
        // The no data message is enabled in getCategories when the actual search starts
        return;
      }
      if (!this.businessType || (typeof(this.businessType) == 'object' && this.businessType.text != newValue)) {
        this.getCategories(newValue);
      }
    },
    businessType(newValue) {
      this.$emit('save-businessType', newValue);
    }
  }
}
</script>
<style lang="scss" scoped>
::v-deep.business-type-services {
  .v-alert__icon{
    display: inline-flex !important;
  }

  span.v-chip.theme--light::after{
    background-color: white !important;
  }

  .v-select:not(.v-autocomplete) .v-select__slot input { visibility: hidden }

  .v-autocomplete.v-select--is-menu-active .v-input__icon--append .v-icon {
    transform: none;
  }

  .v-text-field {
    .v-input__append-inner {
      cursor: pointer;
      .append-text{
        margin: auto;
        position: relative;
        top: -3px;
      }
    }
  }
  .v-input__icon--append .primary--text{
    color: black !important;
  }

  .v-chip {
    font-size: medium;
  }

  .v-alert{
    border-radius: 5px;
    padding: 10px !important;
    color: var(--v-primary-base) !important;
    div{
      color: black !important;
    }
  }
  .suggestion:hover {
    cursor: pointer;
  }
  .suggestion:hover .v-chip__content {
    cursor: pointer;
  }
  .suggestion {
      background-color: white;
      border: 1px solid black;
  }
  .selected-suggestion {
    border-color: var(--v-primary-variation-1-base) !important;
    background-color: var(--v-primary-variation-1-base);
  }
  .service-suggestions{
        .v-chip {
          .v-chip__content {
            white-space: normal;
            height: auto;
          }
        }
  }
  .business-type-input {
    .v-text-field__details {
      display: none
    }
    .v-input__control{
      min-height: auto;
    }
    .v-input__append-inner{
      display: none
    }
  }

  .business-type-selected  .v-input__control .v-input__slot{
      background-color: var(--v-primary-variation-1-base);
      border-radius: 28px;
    }

  .business-type-suggestion   {
     .v-chip {
          .v-chip__content {
            white-space: normal;
            height: auto;
          }
        }
    .selected-suggestion{
      .v-chip__content:hover{
        cursor: context-menu !important;
      }
    }
  }

  .suggestions {
    .layout {
      div {
        .v-chip {
          border: solid 0.5px var(--v-black-base);
        }
        .v-chip--disabled {
          border: solid 0.5px var(--v-primary-base);
        }
      }
    }
  }

  .add-service-flex {
    .layout {
      .flex {
        height: 35px;
        .v-btn {
          max-height: 28px;
          border: solid 0.5px var(--v-black-base);
          background-color: var(--v-white-base);
          .v-btn__content {
            color: var(--v-primary-base);
          }
        }
        .add-service-txt-field{
          max-height: 28px;
          .v-input__control {
            .v-input__slot{
              margin-bottom: 0;
              border-bottom: solid 0.5px var(--v-black-base);
              background-color: var(--v-white-base);
              height: 28px;
              &::before{
                border-style: none;
              }
              &::after{
                border-style: none;
              }
              .v-input__append-inner{
                color: var(--v-primary-base);
              }
              .v-text-field__slot {
                input {
                  padding: 0;
                }
              }
            }
          }
        }
        .service-chips {
          background-color: var(--v-primary-variation-1-base);
          color: var(--v-black-base);
        }
      }
    }
  }
}

::v-deep .v-list > div:first-child .v-list__tile__content{
  font-weight: 500;
  color:var(--v-primary-base)
}
::v-deep .add-service-txt-field {
  @media screen and (max-width: 320px) {
    .v-text-field__slot {
      width: 40px !important;
    }
  }
}

</style>
